/*
  Store Builder craft: no size on arrival (F2, 16 Oct 2026; HOOKS.md and store-kit/README.md, "Product page hooks").
  Deferred after store-builder-craft-colour.js. No network, no markup from strings.
  - A fieldset[data-sb-size] (snippets/variant-main-picker.liquid) keeps no size checked until the shopper picks one,
    through Horizon's re-renders. A picked size stays across a colour change if Horizon's answer has that size.
  - Round 12: Add to Cart stays at full strength while it waits (Horizon's disabled look read 2.3 to 3.3:1). The
    server draws the variant id empty while no size is chosen (blocks/buy-buttons.liquid), so a submit then posts no
    variant, with or without this script. With it, a press or a submit is stopped before Horizon sees it, focus moves to
    the sizes and the hint "Choose a size" stands out, in place. Round 13: the legend keeps "Size"; the hint beside it
    reads "Choose a size" until a pick, then the size (the colour script writes it at the click).
  - The server resolves every variant: this never fills in a size or a variant id, it only clears one.
  - 19 Oct 2026 (Archie, on a phone: "first time I clicked it, it seemed to be buggy"): every press with no size
    marks the hint again and shakes the sizes once (Web Animations, none under reduced motion); a press while Horizon's
    answer to a size or colour pick is in flight is held and pressed again once it lands (4 s at most), so an app that
    adds from the form at the press (UpCart on Grayson) adds the size and colour picked; and a press the layout's early
    guard held before this script was ready (layout/theme.liquid) is pressed again here.
    Post-mortem: UpCart takes Grayson's add at the press and reads the form's variant id there. Until Horizon's answer
    landed that id was empty (no size yet) or the last colour's, so a quick press after a pick added nothing (UpCart:
    "Unable to get product ID from form", 6 of 6 on a 4G phone) or the wrong colour, while Horizon's tick still played.
    Rule: no press reaches Horizon or an app while a pick's answer is in flight.
*/
(() => {
const doc = document;
if (window.sbSize) return;
window.sbSize = true;

const all = (scope, selector) => [...scope.querySelectorAll(selector)];
const set = (el, name, value) => el.getAttribute(name) !== value && el.setAttribute(name, value);
const still = Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);

// The shopper's size per section or dialog: null until they pick one.
const chosen = new WeakMap();
const boxOf = (el) => el.closest('dialog, .shopify-section') || doc.body;
const sizeOf = (variant, fieldset) => {
  const input = fieldset.querySelector('input[data-input-id]');
  const at = Number((input?.getAttribute('data-input-id') || '').split('-')[0]) - 1;
  return variant && Array.isArray(variant.options) ? variant.options[at] : undefined;
};
const resolved = (picker) => {
  try {
    return JSON.parse(picker.querySelector('script[type="application/json"]')?.textContent || 'null');
  } catch {
    return null;
  }
};

// No size yet: no variant to buy, no Shop Pay, and no ?variant= Horizon put in the address for the colour.
function hold(box) {
  for (const pay of all(box, '[ref="acceleratedCheckoutButtonContainer"]')) pay.hidden = true;
  for (const id of all(box, 'product-form-component input[name="id"]')) id.value = '';
  const url = new URL(location.href);
  if (box.matches('.shopify-section') && url.searchParams.has('variant')) {
    url.searchParams.delete('variant');
    history.replaceState(history.state, '', url);
  }
}

// Brings one size fieldset to the shopper's state. `after`: once Horizon has set the buy buttons from its answer.
function settle(fieldset, after) {
  const picker = fieldset.closest('variant-picker');
  if (!picker) return;
  const box = boxOf(picker);
  const radios = all(fieldset, 'input[type="radio"]');
  if (!chosen.has(box)) {
    chosen.set(box, fieldset.getAttribute('data-sb-size') === 'chosen' ? radios.find((r) => r.checked)?.value ?? null : null);
  }
  let size = chosen.get(box);
  if (size !== null && sizeOf(resolved(picker), fieldset) !== size) size = null;
  chosen.set(box, size);
  for (const radio of radios) {
    const on = radio.value === size;
    if (radio.checked !== on) radio.checked = on;
    set(radio, 'data-current-checked', String(on));
  }
  set(fieldset, 'data-sb-size', size === null ? 'pending' : 'chosen');
  // Round 13: the legend keeps "Size"; its hint reads "Choose a size" or the size picked.
  const hint = fieldset.querySelector('.sb-size-hint');
  const text = size ?? hint?.getAttribute('data-sb-prompt');
  if (hint && text && hint.textContent !== text) hint.textContent = text;
  if (after && size === null) hold(box);
}

const settleIn = (scope, after) => all(scope, 'fieldset[data-sb-size]').forEach((fieldset) => settle(fieldset, after));

// A press with no size: focus to the sizes, the prompt marked again and the sizes shaken once, on every press;
// scrolled into view only when it is out of view (19 Oct 2026: a mark alone read as a press that did nothing).
const SHAKE = [0, -8, 8, -6, 6, -3, 0].map((x) => ({ transform: `translateX(${x}px)` }));
const shakes = new WeakMap();
function nudge(fieldset) {
  set(fieldset, 'tabindex', '-1');
  fieldset.removeAttribute('data-sb-nudge');
  const { top, bottom } = fieldset.getBoundingClientRect(); // lays out unmarked: a style keyed to the mark starts again
  fieldset.setAttribute('data-sb-nudge', '');
  fieldset.focus({ preventScroll: true });
  if (top < 0 || bottom > innerHeight) fieldset.scrollIntoView({ block: 'nearest', behavior: still ? 'auto' : 'smooth' });
  if (still || typeof fieldset.animate !== 'function') return;
  shakes.get(fieldset)?.cancel();
  shakes.set(fieldset, fieldset.animate(SHAKE, { duration: 360, easing: 'ease-in-out' }));
}

// Horizon's answer to a pick in flight, per section or dialog (its ProductSelect event's promise): a press meanwhile
// waits here and is pressed again just after the answer lands, once Horizon has set the variant id from it.
const flights = new WeakMap();
function land(box, flight) {
  if (flights.get(box) !== flight) return;
  flights.delete(box);
  clearTimeout(flight.timer);
  if (flight.press?.isConnected) flight.press.click();
}
function inFlight({ target, promise }) {
  if (!(target instanceof Element) || !target.matches('variant-picker') || target.closest('product-card')) return;
  const box = boxOf(target);
  const flight = { press: flights.get(box)?.press ?? null };
  clearTimeout(flights.get(box)?.timer);
  flights.set(box, flight);
  flight.timer = setTimeout(land, 4000, box, flight);
  const after = () => setTimeout(land, 0, box, flight);
  Promise.resolve(promise).then(after, after);
}

// Add to Cart, Horizon's sticky bar's and the kit's; a submit of the product form (Enter, or a script).
const BUY = {
  click: '[ref="addToCartButton"], .sticky-add-to-cart__button, .sb-sticky-buy__button',
  submit: 'form[data-type="add-to-cart-form"]',
};
function stop(event) {
  const from = event.target instanceof Element ? event.target.closest(BUY[event.type]) : null;
  const box = from && boxOf(from);
  const fieldset = box?.querySelector('variant-picker fieldset[data-sb-size="pending"]');
  const flight = box && flights.get(box);
  if (!fieldset && !flight) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  if (fieldset) {
    if (event.type === 'click') nudge(fieldset);
  } else {
    flight.press = event.type === 'click' ? from : from.querySelector('[ref="addToCartButton"]');
    // The tick Horizon shows for an add it queues, so a slow answer never reads as a press that did nothing.
    flight.press?.closest('add-to-cart-component')?.animateAddToCart?.();
  }
}

// A press the layout's early guard held, pressed again once this script is ready (it nudges, waits or adds).
function early() {
  const press = window.sbHeld;
  window.sbHeld = null;
  if (press?.isConnected) press.click();
}

if (!doc.querySelector('variant-picker')) return early();
// Before Horizon's own handlers, which listen on the document.
addEventListener('click', stop, true);
addEventListener('submit', stop, true);
doc.addEventListener('shopify:product:select', inFlight, true);
if (!doc.querySelector('variant-picker fieldset[data-sb-size]')) return early();
settleIn(doc, true);
// The shopper's pick, before Horizon's change handler fetches.
doc.addEventListener('change', ({ target }) => {
  const fieldset = target instanceof HTMLInputElement && target.closest('variant-picker fieldset[data-sb-size]');
  if (!fieldset || !target.checked) return;
  chosen.set(boxOf(fieldset), target.value);
  set(fieldset, 'data-sb-size', 'chosen');
  fieldset.removeAttribute('data-sb-nudge');
}, true);
// Horizon 4.3.0's re-render (variant-picker.js): the picker before paint; the rest after Horizon's own answer.
customElements.whenDefined('variant-picker').then(() => {
  const proto = customElements.get('variant-picker')?.prototype;
  const update = proto?.updateVariantPicker;
  if (typeof update !== 'function') return;
  proto.updateVariantPicker = function updateVariantPicker(...args) {
    const result = update.apply(this, args);
    settleIn(this, false);
    setTimeout(settleIn, 0, this, true);
    return result;
  };
});
early();
})();
